import { computed, ref, toRef, watch } from 'vue';

/**
 * @template T
 * @typedef {object} ClientDataReturn
 * @property {import('vue').Ref<string>} search - Reactive reference to the current search query string.
 * @property {import('vue').ComputedRef<T[]>} datas - Computed ref holding the currently visible (paginated and filtered) data.
 * @property {import('vue').Ref<number>} currentPage - Reactive reference to the current page number.
 * @property {import('vue').ComputedRef<number>} pageCount - Computed ref representing the total number of pages based on filtered data and perPage setting.
 * @property {(newPerPage: number) => void} setPerPage - Function to update the number of items displayed per page.
 * @property {import('vue').Readonly<import('vue').Ref<number>>} currentPerPage - Readonly reactive reference to the current number of items per page.
 * @property {() => void} next - Function to navigate to the next page.
 * @property {() => void} prev - Function to navigate to the previous page.
 * @property {(page: number) => void} goToPage - Function to navigate to a specific page number.
 * @property {import('vue').ComputedRef<boolean>} isFirstPage - Computed ref indicating if the current page is the first page.
 * @property {import('vue').ComputedRef<boolean>} isLastPage - Computed ref indicating if the current page is the last page.
 * @property {import('vue').ComputedRef<number>} fromResult - Computed ref for the starting index of the items shown on the current page (1-based).
 * @property {import('vue').ComputedRef<number>} toResult - Computed ref for the ending index of the items shown on the current page.
 * @property {import('vue').ComputedRef<number>} filteredCount - Computed ref for the total number of items after applying the search filter.
 * @property {import('vue').ComputedRef<number>} totalOriginalCount - Computed ref for the total number of items in the original sourcesRef array.
 * @property {import('vue').ComputedRef<boolean>} isEmpty - Computed ref indicating if there are no items after filtering.
 * @property {import('vue').ComputedRef<boolean>} isNoPages - Computed ref indicating if there are no pages.
 * @property {import('vue').Ref<string | null>} sortKeyRef - Reactive reference to the current sort key.
 * @property {import('vue').Ref<'' | 'asc' | 'desc'>} sortTypeRef - Reactive reference to the current sort type.
 * @property {(key: string | null) => void} setSortKey - Function to update the sort key.
 * @property {(type: '' | 'asc' | 'desc') => void} setSortType - Function to update the sort type.
 */

/**
 * Composable to manage client-side data exploration including search and pagination.
 * @template T
 * @param {import('vue').Ref<T[]> | import('vue').ComputedRef<T[]>} sourcesRef - The reactive array of data items (can be a Ref or ComputedRef).
 * @param {object} [options]
 * @param {import('vue').Ref<number> | number} [options.perPage] - Items per page. Can be a ref. Defaults to 10.
 * @param {Array<string>} [options.searchKeys] - Keys within each object of T to search on. Defaults to an empty array.
 * @param {import('vue').Ref<number> | number} [options.initialPage] - Initial page number. Can be a ref. Defaults to 1.
 * @param {import('vue').Ref<string | null> | string | null} [options.sortKey] - Key to sort by. Defaults to null (no sort).
 * @param {import('vue').Ref<'' | 'asc' | 'desc'> | '' | 'asc' | 'desc'} [options.sortType] - Sort direction ('asc' or 'desc'). Defaults to 'desc' (newest first) if sortKey is provided.
 * @returns {ClientDataReturn<T>} - Data explorer for a list of data
 */
export function useClientDataExplorer(sourcesRef, options = {}) {
  const {
    perPage: optionPerPage = 10,
    searchKeys: optionSearchKeys = [],
    initialPage: optionInitialPage = 1,
    sortKey: optionSortKey = null,
    sortType: optionSortType = '',
  } = options;

  const perPageInternal =
    typeof optionPerPage === 'number'
      ? ref(optionPerPage)
      : toRef(optionPerPage);
  const search = ref('');
  const currentPage =
    typeof optionInitialPage === 'number'
      ? ref(optionInitialPage)
      : toRef(optionInitialPage);

  const sortKeyRef =
    typeof optionSortKey === 'string' || optionSortKey === null
      ? ref(optionSortKey)
      : toRef(optionSortKey);

  const sortTypeRef =
    typeof optionSortType === 'string'
      ? ref(optionSortType)
      : toRef(optionSortType);

  // Ensure searchKeys is always a ref to an array of strings
  const searchKeys = computed(() =>
    Array.isArray(optionSearchKeys) ? optionSearchKeys : []
  );

  const totalOriginalCount = computed(() => sourcesRef.value.length);

  const filteredSources = computed(() => {
    const currentSearchKeys = searchKeys.value;
    let dataToSort = sourcesRef.value;

    // Apply search filter
    if (search.value && currentSearchKeys.length > 0) {
      const query = search.value.toLowerCase();
      dataToSort = dataToSort.filter(item =>
        currentSearchKeys.some(key => {
          if (item && Object.prototype.hasOwnProperty.call(item, key)) {
            const value = item[key];
            return (
              typeof value === 'string' && value.toLowerCase().includes(query)
            );
          }
          return false;
        })
      );
    }

    // Apply sorting
    const currentSortKey = sortKeyRef.value;
    if (currentSortKey) {
      const currentSortType = sortTypeRef.value || 'desc'; // Default to 'desc' if not specified
      dataToSort = [...dataToSort].sort((a, b) => {
        const valA = a[currentSortKey];
        const valB = b[currentSortKey];

        // Handle different data types for comparison
        if (typeof valA === 'number' && typeof valB === 'number') {
          return currentSortType === 'asc' ? valA - valB : valB - valA;
        }
        if (typeof valA === 'string' && typeof valB === 'string') {
          return currentSortType === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        // Add more type handling if needed (e.g., dates)

        // Fallback for other types or mixed types (treat as strings)
        const strValA = String(valA);
        const strValB = String(valB);
        return currentSortType === 'asc'
          ? strValA.localeCompare(strValB)
          : strValB.localeCompare(strValA);
      });
    }

    return dataToSort;
  });

  const filteredCount = computed(() => filteredSources.value.length);

  const pageCount = computed(() => {
    if (perPageInternal.value <= 0) return 1; // Avoid division by zero or negative
    return Math.max(1, Math.ceil(filteredCount.value / perPageInternal.value));
  });

  const datas = computed(() => {
    const start = (currentPage.value - 1) * perPageInternal.value;
    const end = start + perPageInternal.value;
    return filteredSources.value.slice(start, end);
  });

  const isFirstPage = computed(() => currentPage.value === 1);
  const isLastPage = computed(
    () => currentPage.value === pageCount.value || pageCount.value === 0
  );

  const fromResult = computed(() => {
    if (filteredCount.value === 0) return 0;
    return (currentPage.value - 1) * perPageInternal.value + 1;
  });

  const toResult = computed(() => {
    if (filteredCount.value === 0) return 0;
    return Math.min(
      currentPage.value * perPageInternal.value,
      filteredCount.value
    );
  });

  function next() {
    if (!isLastPage.value) {
      currentPage.value++;
    }
  }

  function prev() {
    if (!isFirstPage.value) {
      currentPage.value--;
    }
  }

  function goToPage(page) {
    const pageNum = Number(page);
    if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= pageCount.value) {
      currentPage.value = pageNum;
    } else if (pageNum < 1 && pageCount.value > 0) {
      currentPage.value = 1;
    } else if (pageNum > pageCount.value && pageCount.value > 0) {
      currentPage.value = pageCount.value;
    }
  }

  function setPerPage(newPerPage) {
    const numPerPage = Number(newPerPage);
    if (!Number.isNaN(numPerPage) && numPerPage > 0) {
      perPageInternal.value = numPerPage;
    }
  }

  function setSortKey(key) {
    sortKeyRef.value = key;
  }

  function setSortType(type) {
    if (['', 'asc', 'desc'].includes(type)) {
      sortTypeRef.value = type;
    }
  }

  watch(search, () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1;
    }
  });

  watch(perPageInternal, () => {
    // Recalculate current page if it becomes invalid
    if (pageCount.value > 0 && currentPage.value > pageCount.value) {
      currentPage.value = pageCount.value;
    } else if (currentPage.value < 1 && pageCount.value > 0) {
      currentPage.value = 1;
    } else if (pageCount.value === 0 && currentPage.value !== 1) {
      currentPage.value = 1;
    }
  });

  // Watch for changes in the source data itself or its properties if it's a deep ref
  watch(
    sourcesRef,
    () => {
      if (pageCount.value > 0 && currentPage.value > pageCount.value) {
        currentPage.value = pageCount.value;
      } else if (currentPage.value < 1 && pageCount.value > 0) {
        currentPage.value = 1;
      } else if (pageCount.value === 0 && currentPage.value !== 1) {
        currentPage.value = 1;
      }
    },
    { deep: true }
  );

  // Ensure current page is valid if total pages change
  watch(pageCount, newPageCountValue => {
    if (newPageCountValue > 0) {
      if (currentPage.value > newPageCountValue) {
        currentPage.value = newPageCountValue;
      } else if (currentPage.value < 1) {
        currentPage.value = 1;
      }
    } else {
      // No pages
      currentPage.value = 1;
    }
  });

  const isEmpty = computed(() => filteredCount.value === 0);
  const isNoPages = computed(() => pageCount.value === 0);

  return {
    search,
    datas,
    currentPage,
    pageCount,
    setPerPage,
    currentPerPage: computed(() => perPageInternal.value),
    next,
    prev,
    goToPage,
    isFirstPage,
    isLastPage,
    fromResult,
    toResult,
    filteredCount,
    totalOriginalCount,
    isEmpty,
    isNoPages,
    sortKeyRef: computed(() => sortKeyRef.value),
    sortTypeRef: computed(() => sortTypeRef.value),
    setSortKey,
    setSortType,
  };
}
